// In-portal notifications center. Reads from the backend's
// MeNotificationsController, surfaces a chronological feed with per-row
// mark-as-read + a Mark-all-read affordance when there's anything unread.

import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { usePatient } from '../../context/PatientContext'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import {
  listNotifications,
  markAllRead,
  markRead,
} from '../../services/notificationsService'
import { NotificationsSkeleton } from '../../components/Skeleton'
import { parseUtcDate } from '../../utils/dates'

const KIND_LABEL = {
  SESSION_REMINDER: 'Session',
  MEDICINE_REMINDER: 'Medicine',
  PRACTITIONER_MESSAGE: 'Message',
  PAYMENT_DUE: 'Payment',
  PACK_LOW: 'Pack',
  GOAL_NUDGE: 'Goal',
  SYSTEM: 'System',
}

const KIND_TONE = {
  PAYMENT_DUE: 'urgent',
  SESSION_REMINDER: 'primary',
  MEDICINE_REMINDER: 'primary',
  PRACTITIONER_MESSAGE: 'success',
  PACK_LOW: 'warning',
  GOAL_NUDGE: 'success',
  SYSTEM: 'primary',
}

const UnreadDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  margin-right: 8px;
  flex-shrink: 0;
`

function timeAgo(iso) {
  if (!iso) return ''
  const ms = Date.now() - parseUtcDate(iso).getTime()
  if (Number.isNaN(ms)) return ''
  const sec = Math.round(ms / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 30) return `${day}d ago`
  const mo = Math.round(day / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.round(mo / 12)}y ago`
}

export default function NotificationsCenterPage() {
  const { currentProfileId } = usePatient()
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await listNotifications({ status: 'all', page: 1, pageSize: 50 })
      setItems(Array.isArray(rows) ? rows : [])
    } catch (err) {
      setError('Could not load notifications. Pull to retry.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load, currentProfileId])

  const unreadCount = items.filter((n) => !n.read).length

  async function handleMarkRead(item) {
    if (item.read) return
    setItems((prev) =>
      prev.map((p) =>
        p.id === item.id ? { ...p, read: true, readAt: new Date().toISOString() } : p,
      ),
    )
    try {
      await markRead(item.id)
    } catch {
      // On failure, refetch so server state wins.
      load()
    }
  }

  async function handleMarkAllRead() {
    if (unreadCount === 0) return
    const now = new Date().toISOString()
    setItems((prev) => prev.map((p) => ({ ...p, read: true, readAt: p.readAt || now })))
    try {
      await markAllRead()
    } catch {
      load()
    }
  }

  function handleClick(item) {
    handleMarkRead(item)
    if (item.link) {
      // Internal links start with /; external skip the SPA router.
      if (item.link.startsWith('/')) navigate(item.link)
      else window.location.href = item.link
    }
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home">Back to Today</BackLink>
        <Row $between $gap="12px" $wrap>
          <div>
            <Pill>Notifications</Pill>
            <PageHeading style={{ marginTop: 12 }}>Your notifications</PageHeading>
            <PageSubheading>
              Reminders, messages and updates — newest first.
            </PageSubheading>
          </div>
          {unreadCount > 0 ? (
            <GhostButton onClick={handleMarkAllRead}>Mark all read</GhostButton>
          ) : null}
        </Row>

        {error ? <Muted style={{ color: '#C24D49' }}>{error}</Muted> : null}

        {loading && items.length === 0 ? (
          <NotificationsSkeleton />
        ) : items.length === 0 ? (
          <Card>
            <CardTitle>You're all caught up</CardTitle>
            <Muted>No notifications yet. We'll let you know when there's something to act on.</Muted>
          </Card>
        ) : (
          <Stack $gap="10px">
            {items.map((item) => (
              <Card
                key={item.id}
                as="button"
                onClick={() => handleClick(item)}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: item.read ? undefined : 'rgba(31, 58, 95, 0.04)',
                  border: item.read ? undefined : '1px solid rgba(31, 58, 95, 0.18)',
                }}
              >
                <Row $between $gap="12px" $wrap>
                  <Row $gap="8px" style={{ minWidth: 0, flex: 1, alignItems: 'flex-start' }}>
                    {item.read ? null : <UnreadDot aria-label="Unread" />}
                    <Stack $gap="4px" style={{ minWidth: 0, flex: 1 }}>
                      <Row $gap="8px" $wrap>
                        <Pill $tone={KIND_TONE[item.kind] || 'primary'}>
                          {KIND_LABEL[item.kind] || 'Notification'}
                        </Pill>
                        <Muted style={{ fontSize: 12 }}>{timeAgo(item.createdAt)}</Muted>
                      </Row>
                      <CardTitle style={{ margin: 0, fontSize: 16 }}>{item.title}</CardTitle>
                      {item.body ? <Muted style={{ fontSize: 14 }}>{item.body}</Muted> : null}
                    </Stack>
                  </Row>
                </Row>
              </Card>
            ))}
          </Stack>
        )}

        <Card>
          <CardTitle>Manage what you receive</CardTitle>
          <Muted>
            Choose which kinds of notifications come through (push, email, SMS) on the{' '}
            <a href="/patient-home/profile/notifications">notification preferences</a> page.
          </Muted>
        </Card>
      </Stack>
    </ContentMaxWidth>
  )
}
